import { useState, useEffect } from 'react';
import styled from '@emotion/styled';

import { Link, useNavigate } from 'react-router-dom';

import { Grid, Card, CardHeader, CardContent, IconButton, Button, Divider, CardActions } from '@mui/material';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import BooleanChip from '../../../utils/utilities/BooleanChip';

import { relativeTime, formatPhoneNumber } from '../../../utils/util';

import { BasicFlex, Notes, LabelUpper, P } from '../../../utils/Common';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';

import SellRoundedIcon from '@mui/icons-material/SellRounded';
import PersonPinRoundedIcon from '@mui/icons-material/PersonPinRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import NewReleasesRoundedIcon from '@mui/icons-material/NewReleasesRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';

import NoTimeline from '../../../utils/utilities/Timeline/NoTimeline';

import TimelineWithComments from '../../../utils/utilities/Timeline/TimelineWithComments';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import useSnackBarStore from '../../../store/snackbar';

import { getWigsToAssign, assignWig, unlinkWig } from '../apiService';

import handleErrors from '../../../utils/errorHandler';

// use pageData context
import { usePageData } from '../../../contexts/PageData';

import Dialog from '../../../utils/utilities/Dialog';

import ShareCard from '../../../utils/utilities/Buttons/ShareCard';

import { toUpper } from 'lodash';

import ImageDisplayComponent from '../../../utils/utilities/ImageComponent/ImageDisplay';

import AutoCompleteMulti from '../../../utils/MuiFields/MuiAutocompleteMulti';

import { displayLabelWig } from '../../../utils/displayLabels';

const DetailsTab = () => {
  const [expanded, setExpanded] = useState(false);
  const [selectWig, setSelectWig] = useState(false); // show wig selector
  const [selectedWig, setSelectedWig] = useState(null); // selected wig id

  const [selectedWigInfo, setSelectedWigInfo] = useState({}); // selected wig object

  const queryClient = useQueryClient();

  const showSnackbar = useSnackBarStore(state => state.showSnackbar);

  // main page data
  const { data = {} } = usePageData();

  console.log(data, "wig order data");

  let status = data.fulfillment_status;

  const navigate = useNavigate();

   const handleSelectWig = (wig_id, info) => {
    setSelectedWig(wig_id);
    setSelectedWigInfo(info);
  }

  console.log(selectedWigInfo, "selected wig info");

  const { mutate: assign } = useMutation({
    mutationFn: assignWig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wig_orders'] });
      showSnackbar('Assigned successfully!', 'success');
    },
    onError: (error) => showSnackbar(handleErrors(error), 'error'),
  })

  const { mutate: unlink } = useMutation({
    mutationFn: unlinkWig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wig_orders'] });
      showSnackbar('Removed Assignment successfully!', 'success');
    },
    onError: (error) => showSnackbar(handleErrors(error), 'error'),
  })

   const handleAssign = () => {
    console.log(selectedWigInfo, "selected wig info");
    if (!selectedWigInfo?.wig_id) return;
    assign({
      wig_order_id: data.wig_order_id,
      info: {
        ...selectedWigInfo,
        customer_id: data.customer_id,
        customer_name: data.customer_name,
        order_type: data.order_type
      }
    })
    setSelectWig(false);
  }

  const handleUnlink = () => {
    unlink({
      wig_order_id: data.wig_order_id,
      info: {
        fulfillment_status: data.fulfillment_status,
        inventory_id: data.inventory_id,
        order_type: data.order_type,
        wig_id: data.wig_id
      }
    })
  }

  const customerBlock = [
    {header: "Customer", icon: <AccountBoxOutlinedIcon fontSize='small' />, info: data.customer_name},
    {header: "Phone", icon: <PhoneAndroidRoundedIcon fontSize='small' />, info: formatPhoneNumber(data.customer_phone)},
  ]

  const basicBlock = [
    {header: "Wig Order ID", icon: <ConfirmationNumberRoundedIcon fontSize='small' color="basic" />, info: `#${data.wig_order_num}`},
    
    { header: "Status", info: <MultiStatusChip value={data.fulfillment_status} values={[{value: "pending", label: "Pending", status: "warning"}, {value: "fulfilled", label: "Fulfilled", status: "success"}, {value: "assigned", label: "Assigned", status: "success"}, { value: "cancelled", label: "Cancelled", status: "error"}]} /> },
    {header: "Date Created", info: data.created_at, dataType: "dateTime"},
    {header: "Due Date", info: data.due_date, dataType: "dateTime"},
  ]

  const statusDetails = [
    { header: "Wig Assignment", info: <BooleanChip check={data.is_assigned} values={['Assigned', 'Unassigned']} /> },
  ]

  const wigDetails = [
    {
      header: "Brand", info: <SimpleChip label={data.brand_name} />,
    },
    {
      header: "Wig ID", info: data.wig_id || 'N/A',
    },
    {
      header: "Color", info: data.color,
    },
    {
      header: "Length", info: data.length,
    },
    {
      header: "Size", info: toUpper(data.cap_size),
    },
    {
      header: "Wig Type", info: data.material,
    },
    {
      header: "Hairstyle", info: data.hairstyle,
    },
    {
      header: "Coloring", info: data.coloring,
    },
  ]

  return (
    <Grid container spacing={3}>
      <Grid size={{xs: 12, md: 4}}>
        <Grid container spacing={3}>
          <Grid size={{xs: 12}}>
            <Card variant='outlined'>
              <CardHeader title="Wig Order Summary" subheader={`#${data.wig_order_num}`} action={ data.order_type === 'custom' ? <SimpleChip label="Custom" palette='secondary' /> : null} />
              <CardContent>
                <CardBlockContainer>
                  {customerBlock.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                  <CardSpacer />
                  <CardBlockHeader>Order Details</CardBlockHeader>
                  {basicBlock.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                  <CardSpacer />
                  <CardBlockHeader>Wig Details</CardBlockHeader>
                  {statusDetails.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                </CardBlockContainer>
              </CardContent>
               <Divider />
                <CardActions sx={{justifyContent: "flex-end"}}>
                  {!!data.is_assigned && <Button color="warning" startIcon={<LinkOffRoundedIcon />} onClick={handleUnlink} >Remove Wig Assignment</Button>}
                  {!selectWig ? !data.is_assigned ? <Button variant="text" color="secondary" 
                  disabled={!!data.is_assigned} 
                  startIcon={<AssignmentTurnedInRoundedIcon />} onClick={() => setSelectWig(true)}>Assign Wig ID</Button> : null : (
                    <BasicFlex gap="1rem" dir="column">
                      <P $light my="1rem">
                        Once you assign a wig ID, that wig will be marked as sold.
                      </P>
                    <AutoCompleteMulti 
                      fieldName="wig_id" 
                      url="inventory" 
                      value={selectedWig} 
                      onChange={handleSelectWig} 
                      multiple={false} 
                      displayLabel={displayLabelWig}
                      conditionStr={`&status[$ne]=sold`}
                    />
                    <BasicFlex gap="1rem">
                    <Button variant="outlined" color="error" fullWidth onClick={() => setSelectWig(false)} startIcon={<CancelRoundedIcon />} >Cancel</Button>
                    <Button variant="contained" color="secondary" fullWidth 
                    disabled={!!data.is_assigned}
                     startIcon={<AssignmentTurnedInRoundedIcon />} 
                    onClick={handleAssign} 
                    >Assign Wig ID</Button>
                    </BasicFlex>
                    </BasicFlex>
                  ) }
                </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      <Grid size={{xs: 12, md: 4}}>
        <Card variant='outlined'>
          <CardHeader title="Wig Order Details" />
          <CardContent>
            <CardBlockContainer>
              {wigDetails.map((block) => (
                <CardBlock key={block.header} {...block} />
              ))}
              <CardSpacer />
            </CardBlockContainer>
            <CardBlockHeader>Details</CardBlockHeader>
              <Notes>
                {data.details}
              </Notes>
              <CardSpacer />
              <Divider sx={{my: "1.4rem", mt: "2rem"}} />
              <CardBlockHeader>Media</CardBlockHeader>
              <ImageDisplayComponent media_urls={data?.media_urls} onClick={() => {}} />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{xs: 12, md: 4}}>
        <ShareCard url={`/wig_orders/generate/${data.wig_order_id}`} defaultEmail={data.vendor_email} title="Wig Order Actions" />
        <Card variant='outlined'>
          <CardHeader title={<BasicFlex gap="1rem">Wig Order History <HistoryRoundedIcon /></BasicFlex>} />
          <CardContent>
            <TimelineWithComments resource="wig_orders" resource_id={data.wig_order_id} expand={true} />
          </CardContent>
        </Card>
      </Grid>

 
    </Grid>
  );

};

export default DetailsTab;